'use client';
import { FormEventHandler, useEffect, useState } from 'react';
import AmericanExpressIcon from '@/components/customercenter-ui/svgs/americanExpress';
import DiscoverIcon from '@/components/customercenter-ui/svgs/discover';
import ErrorIcon from '@/components/customercenter-ui/svgs/error';
import MasterCardIcon from '@/components/customercenter-ui/svgs/masterCard';
import SuccessIcon from '@/components/customercenter-ui/svgs/success';
import VisaIcon from '@/components/customercenter-ui/svgs/visaIcon';
import { MONETARY_INPUT_REGEX, ALPHANUMERIC_REGEX, POSITIVE_INTEGER_REGEX } from '../../../../../constants/constant';
import { CARD_NAMES } from '../../customer-detail/constants';

interface InputFieldProps {
  items?: string;
  id: string;
  type?: string;
  classname?: string;
  htmlFor?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  label?: string;
  errors?: any;
  register: any;
  createAccountFlag?: boolean;
  focusFirstName?: boolean;
  focusCardNumber?: boolean;
  onChange?: any;
  rules?: any;
  maxLength?: number;
  hideError?: boolean;
  isNum?: boolean;
  alphaNumeric?: boolean;
  monetaryInput?: boolean;
  handleKeyPress?: (event: React.KeyboardEvent) => void;
  handlePaste?: (event: React.ClipboardEvent) => void;
  cardType?: string;
  setValue?: any;
  validateTwoCharsOrMore?: boolean;
  getValues?: any;
}

const InputField: React.FC<InputFieldProps> = ({
  items,
  id,
  htmlFor,
  type,
  classname,
  value,
  defaultValue,
  disabled,
  label,
  errors,
  register,
  createAccountFlag = false,
  focusFirstName = false,
  focusCardNumber = false,
  onChange,
  rules,
  maxLength,
  hideError,
  isNum,
  alphaNumeric,
  monetaryInput,
  handleKeyPress,
  handlePaste,
  cardType,
  setValue,
  validateTwoCharsOrMore,
  getValues,
}) => {
  const getCardIcon = (creditCardType: string) => {
    switch (creditCardType?.toLowerCase()) {
      case CARD_NAMES.VISA:
        return <VisaIcon className="gbomit h-5 w-9" />;
      case CARD_NAMES.MASTERCARD:
        return <MasterCardIcon className="gbomit h-5 w-9" />;
      case CARD_NAMES.DISCOVER:
        return <DiscoverIcon className="gbomit h-5 w-9" />;
      case CARD_NAMES.AMERICAN_EXPRESS:
        return <AmericanExpressIcon className="gbomit h-5 w-9" />;
      default:
        return null;
    }
  };

  const [isFocus, setIsFocus] = useState(false);
  const [fieldValue, setFieldValue] = useState('');

  useEffect(() => {
    if (focusFirstName || focusCardNumber) {
      document.getElementById(id)?.focus();
    }
  }, []);

  const handleInput: FormEventHandler<HTMLFormElement> = (event) => {
    const input = event.currentTarget;
    let newValue = '';
    if (
      id === 'line1' ||
      id === 'line2' ||
      id === 'streetName' ||
      id === 'addressLine2' ||
      id === 'additionalAddressInfo'
    ) {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s/#.,;:'\-\/\u00B0]+/g, '');
    } else if (id === 'zip' || id === 'postalCode') {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s-]/g, '');
    } else if (id === 'firstName' || id === 'lastName') {
      const currentValue = input?.value?.replace(/[^a-zA-Z\s-]/g, '');

      const firstName = id === 'firstName' ? currentValue : getValues('firstName') || '';

      const lastName = id === 'lastName' ? currentValue : getValues('lastName') || '';

      const totalLength = firstName?.length + lastName?.length;

      // block additional characters after 28
      if (totalLength > 28) {
        input.value = id === 'firstName' ? getValues('firstName') || '' : getValues('lastName') || '';

        return;
      }

      newValue = currentValue;
    } else {
      newValue = input?.value?.replace(/[^a-zA-Z0-9\s]/g, '');
      event.currentTarget.value = newValue;
    }
    if (input?.value !== newValue) {
      input.value = newValue;
      const changeEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  };

  const specialCharsNotAllowedFields = [
    'firstName',
    'lastName',
    'companyName',
    'streetName',
    'line1',
    'line2',
    'addressLine2',
    'city',
    'additionalAddressInfo',
    'zip',
    'postalCode',
  ];

  const handleSpecialChars = (event: any) => {
    const input = event.currentTarget;
    let value = input.value;

    if (specialCharsNotAllowedFields?.includes(id)) {
      handleInput(event);
      return;
    }

    if (id === 'confirmationId') {
      // Allow letters, numbers, and hyphens (limit hyphens to 2)
      value = value.replace(/[^a-zA-Z0-9-]/g, '');
      let hyphenCount = 0;
      value = value.replace(/-/g, () => (hyphenCount++ < 2 ? '-' : ''));
    } else if (id === 'customerId') {
      // Allow only numbers and hyphens (limit hyphens to 2)
      value = value.replace(/[^a-zA-Z0-9-]/g, '');
      let hyphenCount = 0;
      value = value.replace(/-/g, () => (hyphenCount++ < 2 ? '-' : ''));
    }
    if (input.value !== value) {
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  const combinedKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    function simulateValueAfterKeyPress(
      value: string,
      key: string,
      selectionStart: number | null,
      selectionEnd: number | null,
    ): string {
      if (selectionStart === null || selectionEnd === null) return value;

      const before = value.substring(0, selectionStart);
      const after = value.substring(selectionEnd);
      return before + key + after;
    }
    const { key, currentTarget } = event;
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    if (allowedKeys.includes(key)) {
      return;
    }
    const valueAfterKeyPress = simulateValueAfterKeyPress(
      currentTarget.value,
      key,
      currentTarget.selectionStart,
      currentTarget.selectionEnd,
    );

    if (isNum && !POSITIVE_INTEGER_REGEX.test(valueAfterKeyPress)) {
      event.preventDefault();
    }

    if (monetaryInput && !MONETARY_INPUT_REGEX.test(valueAfterKeyPress)) {
      event.preventDefault();
    }

    if (alphaNumeric && !ALPHANUMERIC_REGEX.test(event?.key)) {
      event.preventDefault();
    }

    if (handleKeyPress) {
      handleKeyPress(event);
    }
  };

  const combinedPasteHandler = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const clipboardData = event.clipboardData.getData('text')?.trim();
    if (isNum && !POSITIVE_INTEGER_REGEX.test(clipboardData)) {
      return;
    }
    if (monetaryInput && !MONETARY_INPUT_REGEX.test(clipboardData)) {
      return;
    }
    if (alphaNumeric && !ALPHANUMERIC_REGEX.test(clipboardData)) {
      return;
    }
    if (handlePaste) {
      handlePaste(event);
    }
    const input = event.currentTarget;
    if (maxLength) {
      const newValue = clipboardData?.slice(0, maxLength);
      input.value = newValue;
      setValue?.(id, newValue);
    } else {
      input.value = clipboardData;
      setValue?.(id, clipboardData);
    }
  };

  let labelPosition;

  if (label === 'password' || label === 'newPassword') {
    labelPosition = createAccountFlag ? 'right-32 md:right-16' : 'right-16';
  } else if (label === 'securityCode') {
    labelPosition = 'right-2';
  } else {
    labelPosition = 'right-4';
  }

  return (
    // <div className="mb-3 h-auto">
    <div className={`relative w-full ${!hideError && errors?.[id] && 'mb-10'}`}>
      <input
        id={id}
        className={
          'peer block appearance-none rounded-lg border bg-transparent p-4 pr-[2.5rem] text-base text-black disabled:bg-gray-200 ' +
          (errors?.[id] ? ' border-2 border-scarletRed ' : ' border-foggyGray ') +
          classname
        }
        placeholder=""
        value={value}
        defaultValue={defaultValue}
        type={type}
        disabled={disabled}
        onKeyDown={() => {
          if (type === 'date') {
            setIsFocus(true);
          }
        }}
        {...register(id, {
          onChange: (e: any) => {
            handleSpecialChars(e);
            setFieldValue(e?.target?.value?.trim());
            onChange?.(e);
          },
          ...rules,
          validate: (value: any) => {
            if (alphaNumeric && !ALPHANUMERIC_REGEX.test(value)) {
              return false;
            }
            return true;
          },
          isDirty: true,
        })}
        onBeforeInput={(e) => {
          if (validateTwoCharsOrMore) {
            const inputEvent = e as React.FormEvent<HTMLInputElement>;
            const input = inputEvent?.currentTarget;
            const currentValue = input?.value;

            let currentLength = currentValue?.length;
            if (e?.nativeEvent && 'data' in e?.nativeEvent && e?.nativeEvent?.data) {
              currentLength = currentValue?.length + 1;
            }
            if (currentLength >= 2) {
              setIsFocus(true);
            } else {
              setIsFocus(false);
            }
          } else {
            setIsFocus(true);
          }
        }}
        maxLength={maxLength}
        onKeyPress={combinedKeyPressHandler}
        onPaste={combinedPasteHandler}
        onInput={handleSpecialChars}
      />
      <label
        htmlFor={htmlFor}
        className={
          'absolute start-1 top-2 ml-1 origin-[0] -translate-y-4 scale-75 transform bg-white px-2 text-base duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4' +
          (errors?.[id] && ' text-scarletRed ')
        }
      >
        {items}
      </label>
      {errors &&
        (errors[id] ? (
          <section className={`absolute top-5 ${labelPosition}`}>
            <ErrorIcon className="h-5 w-5" />
          </section>
        ) : (
          isFocus && (
            <section
              className={
                'absolute top-5 ' +
                (label === 'password' || label === 'newPassword'
                  ? createAccountFlag
                    ? ' right-32 md:right-16 '
                    : ' right-16 '
                  : 'right-4 ')
              }
            >
              {label === 'cardNumber' && cardType !== '' && getCardIcon(cardType ?? '')}
              {label !== 'cardNumber' && fieldValue && <SuccessIcon className="h-5 w-5" />}
            </section>
          )
        ))}
      {!hideError && errors?.[id] && <p className="absolute mt-2 text-sm text-scarletRed">{errors[id]?.message}</p>}
    </div>
  );
};

export default InputField;
